import supabase from "@/services/supabaseClient";
import {
  CLINICIAN_SIGNATURE_PRESENTATION,
  createDoctorStampOverlayMarkup,
  DOCTOR_STAMP_PRESENTATION,
} from "@/components/prescriptions/doctorStampPresentation.js";

const REFERRAL_DOCUMENT_BUCKET = "referral-documents";
const PDF_MARGIN_X = 44;
const PDF_MARGIN_TOP = 44;
const PDF_MARGIN_BOTTOM = 44;
const PAGE_FONT = "helvetica";
const TEXT_COLOR = [17, 24, 39];
const MUTED_COLOR = [75, 85, 99];
const RULE_COLOR = [209, 213, 219];
const REFERRAL_STAMP_BASE = "/icons/appointments/e-prescription/afrimed-dr-stamp-base-flat.png";
const DOCUMENT_LOGO_MAX_WIDTH_PX = 400;
const DOCUMENT_LOGO_MAX_HEIGHT_PX = 113.4;
const CSS_PX_TO_PDF_PT = 72 / 96;

// Shared AfriMed document shell for non-clinical documents that must retain the
// same PDF identity, margins, metadata treatment and export behaviour.
export async function generateAfriMedDocumentPdf({ title, sections = [] } = {}) {
  const jsPdfMod = await import("jspdf");
  const JsPdf = jsPdfMod?.jsPDF || jsPdfMod?.default;
  const pdf = new JsPdf({ unit: "pt", format: "a4", compress: true });
  const cursor = createPdfCursor(pdf);
  const logoDataUrl = await rasterizeImageToPngDataUrl("/assets/images/afrimed_logo.svg").catch(() => "");
  pdf.setFillColor(241, 247, 255);
  pdf.rect(0, 0, cursor.pageWidth, 132, "F");
  if (logoDataUrl) pdf.addImage(logoDataUrl, "PNG", PDF_MARGIN_X, cursor.y, 109, 31);
  cursor.y += 54;
  drawTextBlock(pdf, cursor, safeText(title, "Document"), { size: 17, fontStyle: "bold", lineHeight: 24 });
  cursor.y = 166;
  sections.forEach((section, index) => {
    if (section?.title) drawTextBlock(pdf, cursor, safeText(section.title), { size: 10, fontStyle: "bold", color: MUTED_COLOR, lineHeight: 14 });
    (section?.rows || []).forEach(([label, value]) => drawMetaRow(pdf, cursor, safeText(label), safeText(value)));
    if (index < sections.length - 1) { cursor.y += 6; drawRule(pdf, cursor); cursor.y += 8; }
  });
  pdf.setFont(PAGE_FONT, "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(...MUTED_COLOR);
  pdf.text("© 2026 AfriMed™ Telehealth. POPIA • HIPAA • GDPR Compliant Digital Health Infrastructure.", PDF_MARGIN_X, cursor.pageHeight - PDF_MARGIN_BOTTOM + 12);
  return pdf.output("blob");
}

export function downloadAfriMedDocumentPdf(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url; link.download = fileName; document.body.appendChild(link); link.click(); link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function printAfriMedDocumentPdf(blob) {
  const url = URL.createObjectURL(blob);
  const popup = window.open(url, "_blank", "noopener,noreferrer");
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return Boolean(popup);
}

function safeText(value, fallback = "—") {
  const text = String(value || "").trim();
  return text || fallback;
}

function formatLongDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

function formatDob(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatDoctorLabel(value) {
  const text = String(value || "")
    .replace(/^dr\.?\s+/i, "")
    .trim();
  if (!text) return "Doctor";
  const parts = text.split(/\s+/).filter(Boolean);
  const surname = parts[parts.length - 1] || text;
  return `Dr ${surname}`;
}

function formatReferralHeading(urgency) {
  return String(urgency || "").trim().toLowerCase() === "urgent"
    ? "Urgent referral"
    : "Referral";
}

function formatRecipientLine(name, specialty) {
  const recipient = String(name || "").trim();
  const governedSpecialty = String(specialty || "").trim();
  if (!recipient) return "";
  if (!governedSpecialty) return recipient;
  const specialistLabel = /\bspecialist\b/i.test(governedSpecialty)
    ? governedSpecialty
    : `${governedSpecialty} Specialist`;
  return `${recipient} • ${specialistLabel}`;
}

function formatUrgency(value) {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return "Routine";
  if (text === "soon") return "Soon";
  if (text === "urgent") return "Urgent";
  return "Routine";
}

function normalizeParagraphs(value, fallback = "—") {
  const text = String(value || "").replace(/\r/g, "").trim();
  if (!text) return [fallback];

  const chunks = text
    .split(/\n{2,}/)
    .map((entry) => entry.trim())
    .filter(Boolean);

  return chunks.length > 0 ? chunks : [fallback];
}

function normalizeMedicationLines(value) {
  const text = String(value || "").replace(/\r/g, "").trim();
  if (!text) return [];

  const rawLines = text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (rawLines.length > 1) return rawLines;
  return text
    .split(/\s*;\s*/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function stripLeadingLabel(value) {
  return String(value || "")
    .replace(/^[A-Za-z][A-Za-z /&()-]*:\s*/i, "")
    .trim();
}

function buildStructuredSections(referral) {
  const reasonText = String(referral?.reason || "").replace(/\r/g, "").trim();
  const historyText = String(referral?.clinical_history || "").replace(/\r/g, "").trim();
  const combinedSource =
    historyText.length >= reasonText.length ? historyText : reasonText;

  const labeledSections = {};
  const headingPattern =
    /(Reason for Referral|Clinical History(?:\s*&\s*Findings)?|Current Medications?|Specific Request|Urgency)\s*:/gi;

  const matches = Array.from(combinedSource.matchAll(headingPattern));
  for (let index = 0; index < matches.length; index += 1) {
    const current = matches[index];
    const next = matches[index + 1];
    const key = String(current[1] || "").trim().toLowerCase();
    const start = current.index + current[0].length;
    const end = next ? next.index : combinedSource.length;
    labeledSections[key] = combinedSource.slice(start, end).trim();
  }

  const reason =
    labeledSections["reason for referral"] ||
    (reasonText && reasonText !== historyText ? reasonText : "");
  const clinicalHistory =
    labeledSections["clinical history"] ||
    labeledSections["clinical history & findings"] ||
    historyText ||
    reasonText;
  const medications =
    labeledSections["current medications"] ||
    labeledSections["current medication"] ||
    "";

  return {
    reason: stripLeadingLabel(reason || clinicalHistory || "Not specified"),
    clinicalHistory: stripLeadingLabel(clinicalHistory || reasonText || "Not specified"),
    currentMedications: stripLeadingLabel(medications || "Not specified"),
    specificRequest: safeText(referral?.specific_request, "Not specified"),
    urgency: formatUrgency(referral?.urgency),
  };
}

async function rasterizeImageToPngDataUrl(sourceUrl = "") {
  const cleanUrl = String(sourceUrl || "").trim();
  if (!cleanUrl) return "";

  const response = await fetch(cleanUrl);
  if (!response.ok) {
    throw new Error(`Could not load image asset: ${cleanUrl}`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    const image = await new Promise((resolve, reject) => {
      const nextImage = new window.Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () => reject(new Error(`Could not decode image asset: ${cleanUrl}`));
      nextImage.src = objectUrl;
    });

    const width = Math.max(1, Number(image.naturalWidth || image.width || 1));
    const height = Math.max(1, Number(image.naturalHeight || image.height || 1));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Could not create image canvas.");
    context.drawImage(image, 0, 0, width, height);
    return canvas.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function rasterizeDoctorStampToPngDataUrl({ baseSrc, doctorName, registrationNumber }) {
  const baseDataUrl = await rasterizeImageToPngDataUrl(baseSrc).catch(() => "");
  if (!baseDataUrl) return "";

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${DOCTOR_STAMP_PRESENTATION.viewBox}" width="${DOCTOR_STAMP_PRESENTATION.sizePx}" height="${DOCTOR_STAMP_PRESENTATION.sizePx}">
    <image href="${baseDataUrl}" x="0" y="0" width="100" height="100" preserveAspectRatio="xMidYMid meet" />
    ${createDoctorStampOverlayMarkup({ doctorName, regNo: registrationNumber, idPrefix: "referral-download-stamp" })}
  </svg>`;
  return rasterizeImageToPngDataUrl(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`).catch(() => "");
}

function fitImageWithinBounds(pdf, imageDataUrl, maxWidth, maxHeight) {
  let width = maxWidth;
  let height = maxHeight;
  try {
    const image = pdf.getImageProperties(imageDataUrl);
    const aspectRatio = Number(image?.width) / Number(image?.height);
    if (Number.isFinite(aspectRatio) && aspectRatio > 0) {
      width = Math.min(maxWidth, maxHeight * aspectRatio);
      height = width / aspectRatio;
    }
  } catch {
    // The canonical bounding box still protects a valid rasterised asset.
  }
  return { width, height };
}

function getCopy(copy, key, fallback) {
  return String(copy?.[key] || "").trim() || fallback;
}

function buildDocumentContext(referral, documentContext = {}, copy = {}) {
  const structured = buildStructuredSections(referral);
  const toLine =
    formatRecipientLine(
      documentContext?.acceptedDoctorName,
      documentContext?.acceptedDoctorSpecialty,
    ) ||
    safeText(referral?.note_text, "") ||
    (safeText(referral?.to_specialty_label, "")
      ? `${safeText(referral?.to_specialty_label)} specialist`
      : "Specialist");

  const patientName = safeText(documentContext?.patientName, "");
  const patientDob = formatDob(documentContext?.patientDob);
  const patientLine = patientName
    ? `${patientName}${patientDob ? ` (DOB: ${patientDob})` : ""}`
    : "—";

  const doctorLabel = formatDoctorLabel(documentContext?.doctorName);
  const facilityName = safeText(documentContext?.facilityName, "");
  const facilityAddress = safeText(documentContext?.facilityAddress, "");

  return {
    heading:
      String(referral?.urgency || "").trim().toLowerCase() === "urgent"
        ? getCopy(copy, "headingUrgent", "Urgent referral")
        : getCopy(copy, "headingDefault", "Referral"),
    subtitle: getCopy(copy, "subtitle", "Your referral document issued by your doctor."),
    dateLabel: formatLongDate(referral?.created_at),
    toLine,
    doctorLabel,
    patientLine,
    facilityName,
    facilityAddress,
    doctorRegistrationNumber: safeText(documentContext?.doctorRegistrationNumber, ""),
    structured,
  };
}

function createPdfCursor(pdf) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  return {
    pageWidth,
    pageHeight,
    contentWidth: pageWidth - PDF_MARGIN_X * 2,
    y: PDF_MARGIN_TOP,
  };
}

function ensurePageSpace(pdf, cursor, requiredHeight) {
  if (cursor.y + requiredHeight <= cursor.pageHeight - PDF_MARGIN_BOTTOM) return;
  pdf.addPage();
  cursor.y = PDF_MARGIN_TOP;
}

function drawRule(pdf, cursor) {
  pdf.setDrawColor(...RULE_COLOR);
  pdf.setLineWidth(0.8);
  pdf.line(PDF_MARGIN_X, cursor.y, cursor.pageWidth - PDF_MARGIN_X, cursor.y);
  cursor.y += 18;
}

function drawTextBlock(pdf, cursor, lines, options = {}) {
  const {
    size = 11,
    color = TEXT_COLOR,
    lineHeight = 15,
    fontStyle = "normal",
    indent = 0,
  } = options;

  pdf.setFont(PAGE_FONT, fontStyle);
  pdf.setFontSize(size);
  pdf.setTextColor(...color);

  const normalizedLines = Array.isArray(lines) ? lines : [String(lines || "")];
  normalizedLines.forEach((line) => {
    ensurePageSpace(pdf, cursor, lineHeight);
    pdf.text(String(line || ""), PDF_MARGIN_X + indent, cursor.y);
    cursor.y += lineHeight;
  });
}

function drawParagraphs(pdf, cursor, paragraphs, options = {}) {
  const {
    size = 11,
    color = TEXT_COLOR,
    lineHeight = 15,
    paragraphGap = 5,
    indent = 0,
  } = options;

  pdf.setFont(PAGE_FONT, "normal");
  pdf.setFontSize(size);
  pdf.setTextColor(...color);

  const maxWidth = cursor.contentWidth - indent;
  paragraphs.forEach((paragraph, index) => {
    const split = pdf.splitTextToSize(String(paragraph || ""), maxWidth);
    split.forEach((line) => {
      ensurePageSpace(pdf, cursor, lineHeight);
      pdf.text(String(line || ""), PDF_MARGIN_X + indent, cursor.y);
      cursor.y += lineHeight;
    });
    if (index < paragraphs.length - 1) {
      cursor.y += paragraphGap;
    }
  });
}

function drawMedicationList(pdf, cursor, items) {
  const entries = items.length > 0 ? items : ["Not specified"];
  pdf.setFont(PAGE_FONT, "normal");
  pdf.setFontSize(11);
  pdf.setTextColor(...TEXT_COLOR);

  entries.forEach((item) => {
    const split = pdf.splitTextToSize(String(item || ""), cursor.contentWidth - 14);
    ensurePageSpace(pdf, cursor, split.length * 15 + 1);
    split.forEach((line, index) => {
      const prefix = index === 0 ? "\u2022" : "";
      const x = PDF_MARGIN_X + (index === 0 ? 0 : 14);
      pdf.text(prefix ? `${prefix} ${line}` : String(line || ""), x, cursor.y);
      cursor.y += 15;
    });
  });
}

function drawSection(pdf, cursor, label, content, mode = "paragraphs") {
  const sectionTopGap = label === "Reason for referral" ? 0 : 6;
  cursor.y += sectionTopGap;
  drawTextBlock(pdf, cursor, label, {
    size: 12,
    fontStyle: "bold",
    lineHeight: 16,
  });

  if (mode === "list") {
    drawMedicationList(pdf, cursor, normalizeMedicationLines(content));
    cursor.y += 6;
    return;
  }

  drawParagraphs(pdf, cursor, normalizeParagraphs(content), {
    size: 11,
    lineHeight: 15,
    paragraphGap: 5,
  });
  cursor.y += 6;
}

async function drawHeaderBranding(pdf, cursor, logoDataUrl, context) {
  const contentTop = cursor.y;
  let logoBottom = contentTop;
  let rightBottom = contentTop;

  if (logoDataUrl) {
    const { width: logoWidth, height: logoHeight } = fitImageWithinBounds(
      pdf,
      logoDataUrl,
      DOCUMENT_LOGO_MAX_WIDTH_PX * CSS_PX_TO_PDF_PT,
      DOCUMENT_LOGO_MAX_HEIGHT_PX * CSS_PX_TO_PDF_PT,
    );

    pdf.addImage(logoDataUrl, "PNG", PDF_MARGIN_X, contentTop, logoWidth, logoHeight);
    logoBottom = contentTop + logoHeight;
  }

  const rightLines = [context.facilityName || "", context.facilityAddress || ""].filter(
    Boolean
  );

  if (rightLines.length > 0) {
    const rightX = cursor.pageWidth - PDF_MARGIN_X;
    let rightY = contentTop + 10;

    rightLines.forEach((line, index) => {
      pdf.setFont(PAGE_FONT, index === 0 ? "bold" : "normal");
      pdf.setFontSize(index === 0 ? 10.5 : 9.5);
      pdf.setTextColor(...(index === 0 ? TEXT_COLOR : MUTED_COLOR));
      const split = pdf.splitTextToSize(String(line || ""), 185);
      split.forEach((entry) => {
        pdf.text(String(entry || ""), rightX, rightY, { align: "right" });
        rightY += index === 0 ? 12 : 12;
      });
    });

    rightBottom = Math.max(rightBottom, rightY);
  }

  cursor.y = Math.max(logoBottom, rightBottom, contentTop + 28) + 12;
}

function drawMetaRow(pdf, cursor, label, value) {
  const labelText = `${label}:`;
  pdf.setFont(PAGE_FONT, "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(...TEXT_COLOR);
  pdf.text(labelText, PDF_MARGIN_X, cursor.y);

  const labelWidth = pdf.getTextWidth(labelText) + 6;
  pdf.setFont(PAGE_FONT, "normal");
  const split = pdf.splitTextToSize(String(value || "—"), cursor.contentWidth - labelWidth);
  split.forEach((line, index) => {
    ensurePageSpace(pdf, cursor, 15);
    const x = PDF_MARGIN_X + (index === 0 ? labelWidth : 0);
    pdf.text(String(line || ""), x, cursor.y);
    cursor.y += 15;
  });
}

function measureSignatureBlockHeight(pdf, context) {
  let total = 18 + 14 + 18;
  if (context.doctorRegistrationNumber) total += 14;
  if (context.signatureDataUrl) total += 58;
  else total += 18;
  return total + 30;
}

function drawSignatureBlock(pdf, cursor, context) {
  const neededHeight = measureSignatureBlockHeight(pdf, context);
  ensurePageSpace(pdf, cursor, neededHeight);

  drawTextBlock(pdf, cursor, getCopy(context.copy, "signature", "Physician signature"), {
    size: 12,
    fontStyle: "bold",
    lineHeight: 16,
  });
  drawTextBlock(
    pdf,
    cursor,
    getCopy(context.copy, "signatureIssuedBy", "Issued by {doctorLabel}.").replace(
      "{doctorLabel}",
      context.doctorLabel
    ),
    {
      size: 10,
      color: MUTED_COLOR,
      lineHeight: 14,
    }
  );

  if (context.doctorRegistrationNumber) {
    drawTextBlock(pdf, cursor, `Registration: ${context.doctorRegistrationNumber}`, {
      size: 9.5,
      color: MUTED_COLOR,
      lineHeight: 13,
    });
  }

  const signatureTop = cursor.y;
  if (context.signatureDataUrl) {
    const signature = fitImageWithinBounds(
      pdf,
      context.signatureDataUrl,
      CLINICIAN_SIGNATURE_PRESENTATION.widthPx * CSS_PX_TO_PDF_PT,
      CLINICIAN_SIGNATURE_PRESENTATION.heightPx * CSS_PX_TO_PDF_PT,
    );
    pdf.addImage(
      context.signatureDataUrl,
      "PNG",
      PDF_MARGIN_X,
      cursor.y + CLINICIAN_SIGNATURE_PRESENTATION.nudgeYPx * CSS_PX_TO_PDF_PT,
      signature.width,
      signature.height,
    );
  } else {
    drawTextBlock(pdf, cursor, getCopy(context.copy, "signatureOnFile", "Signature on file."), {
      size: 10,
      color: MUTED_COLOR,
      lineHeight: 14,
    });
  }

  if (context.stampDataUrl) {
    const stampSize = DOCTOR_STAMP_PRESENTATION.sizePx * CSS_PX_TO_PDF_PT;
    const stampX = cursor.pageWidth - PDF_MARGIN_X - stampSize;
    const stampY = signatureTop + DOCTOR_STAMP_PRESENTATION.nudgeYPx * CSS_PX_TO_PDF_PT;
    pdf.addImage(context.stampDataUrl, "PNG", stampX, stampY, stampSize, stampSize);
  }
  cursor.y = Math.max(
    cursor.y,
    signatureTop + Math.max(
      CLINICIAN_SIGNATURE_PRESENTATION.heightPx,
      DOCTOR_STAMP_PRESENTATION.sizePx,
    ) * CSS_PX_TO_PDF_PT + CLINICIAN_SIGNATURE_PRESENTATION.nudgeYPx * CSS_PX_TO_PDF_PT,
  );

  pdf.setDrawColor(...RULE_COLOR);
  pdf.setLineDashPattern([2, 2], 0);
  pdf.line(PDF_MARGIN_X, cursor.y + 2, cursor.pageWidth - PDF_MARGIN_X, cursor.y + 2);
  pdf.setLineDashPattern([], 0);
  cursor.y += 16;
  drawTextBlock(pdf, cursor, getCopy(context.copy, "clinicalResponsibility", "Powered by AfriMed™. The referring clinician remains responsible for clinical decisions."), {
    size: 9.5,
    color: MUTED_COLOR,
    lineHeight: 13,
  });
}

export async function generateReferralPdfBlob({ referral, documentContext = {}, copy = {} }) {
  const jsPdfMod = await import("jspdf");
  const JsPdf = jsPdfMod?.jsPDF || jsPdfMod?.default;
  const pdf = new JsPdf({ unit: "pt", format: "a4", compress: true });
  const cursor = createPdfCursor(pdf);

  const context = buildDocumentContext(referral, documentContext, copy);
  const [practiceLogoDataUrl, doctorSignatureUrl, stampDataUrl] = await Promise.all([
    String(documentContext?.practiceLogoUrl || "").trim()
      ? rasterizeImageToPngDataUrl(documentContext.practiceLogoUrl).catch(() => "")
      : Promise.resolve(""),
    String(documentContext?.doctorSignatureUrl || "").trim()
      ? rasterizeImageToPngDataUrl(documentContext.doctorSignatureUrl).catch(() => "")
      : Promise.resolve(""),
    rasterizeDoctorStampToPngDataUrl({
      baseSrc: documentContext?.doctorStampBaseSrc || REFERRAL_STAMP_BASE,
      doctorName: context.doctorLabel,
      registrationNumber: context.doctorRegistrationNumber,
    }),
  ]);

  await drawHeaderBranding(pdf, cursor, practiceLogoDataUrl, context);

  drawTextBlock(pdf, cursor, context.heading, {
    size: 16,
    fontStyle: "bold",
    lineHeight: 20,
  });
  drawTextBlock(pdf, cursor, context.subtitle, {
    size: 10,
    color: MUTED_COLOR,
    lineHeight: 14,
  });
  cursor.y += 8;

  drawMetaRow(pdf, cursor, getCopy(copy, "date", "Date"), context.dateLabel);
  drawMetaRow(pdf, cursor, getCopy(copy, "to", "To"), context.toLine);
  drawMetaRow(pdf, cursor, getCopy(copy, "from", "From"), context.doctorLabel);
  drawMetaRow(pdf, cursor, getCopy(copy, "patient", "Patient"), context.patientLine);

  cursor.y += 6;
  drawRule(pdf, cursor);

  drawSection(pdf, cursor, getCopy(copy, "reason", "Reason for referral"), context.structured.reason);
  drawSection(
    pdf,
    cursor,
    getCopy(copy, "clinicalHistory", "Clinical history & findings"),
    context.structured.clinicalHistory
  );
  drawSection(
    pdf,
    cursor,
    getCopy(copy, "currentMedications", "Current medications"),
    context.structured.currentMedications,
    "list"
  );
  drawSection(pdf, cursor, getCopy(copy, "specificRequest", "Specific request"), context.structured.specificRequest);
  drawSection(pdf, cursor, getCopy(copy, "urgency", "Urgency"), context.structured.urgency);

  drawRule(pdf, cursor);

  drawSignatureBlock(pdf, cursor, {
    ...context,
    copy,
    signatureDataUrl: doctorSignatureUrl,
    stampDataUrl,
  });

  return pdf.output("blob");
}

export async function uploadReferralPdfAndCreateSignedUrl({
  referral,
  documentContext = {},
  patientUserId = "",
  copy = {},
}) {
  const cleanPatientUserId = String(patientUserId || "").trim();
  const cleanReferralId = String(referral?.id || "").trim();
  if (!cleanPatientUserId || !cleanReferralId) {
    throw new Error("Missing referral document context.");
  }

  const pdfBlob = await generateReferralPdfBlob({ referral, documentContext, copy });
  const file = new File([pdfBlob], `referral-${cleanReferralId}.pdf`, {
    type: "application/pdf",
  });
  const path = `patients/${cleanPatientUserId}/referrals/${cleanReferralId}/referral-document.pdf`;

  const { error: uploadError } = await supabase.storage
    .from(REFERRAL_DOCUMENT_BUCKET)
    .upload(path, file, {
      upsert: true,
      cacheControl: "3600",
      contentType: "application/pdf",
    });

  if (uploadError) throw uploadError;

  const { data, error } = await supabase.storage
    .from(REFERRAL_DOCUMENT_BUCKET)
    .createSignedUrl(path, 60 * 60 * 24 * 7);

  if (error) throw error;

  return {
    fileName: file.name,
    filePath: path,
    signedUrl: String(data?.signedUrl || "").trim(),
  };
}

export async function ensureReferralPdfStored({
  referral,
  documentContext = {},
  patientUserId = "",
  copy = {},
}) {
  const cleanPatientUserId = String(patientUserId || "").trim();
  const cleanReferralId = String(referral?.id || "").trim();
  if (!cleanPatientUserId || !cleanReferralId) {
    throw new Error("Missing referral document context.");
  }

  const pdfBlob = await generateReferralPdfBlob({ referral, documentContext, copy });
  const file = new File([pdfBlob], `referral-${cleanReferralId}.pdf`, {
    type: "application/pdf",
  });
  const path = `patients/${cleanPatientUserId}/referrals/${cleanReferralId}/referral-document.pdf`;

  const { error: uploadError } = await supabase.storage
    .from(REFERRAL_DOCUMENT_BUCKET)
    .upload(path, file, {
      upsert: true,
      cacheControl: "3600",
      contentType: "application/pdf",
    });

  if (uploadError) throw uploadError;

  return {
    fileName: file.name,
    filePath: path,
  };
}
